import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { createFileRoute } from "@tanstack/react-router";
import { AuthContext } from "react-oauth2-code-pkce";

import {
  clearActiveTenantCookie,
  clearClientAuthState,
  consumeLogoutIntent,
  fetchCsrfToken,
  readLogoutIntentNonce,
  revokeTokens,
} from "../../auth/utils";
import { LOGOUT_URL } from "../../config/oauth.config";
import { purgeAllChatHistory } from "../../lib/chatHistoryStorage";

export const Route = createFileRoute("/auth/logout")({
  component: LogoutPage,
});

type LogoutStatus = "signing-out" | "confirm" | "failed";

// Submits a hidden form rather than navigating or fetching.
//
// LOA-1617: the backend logout endpoint is a CSRF-protected POST, so a plain
// navigation no longer works. A form POST still sends the API-domain session
// cookie and still lets the browser follow the backend's redirect chain
// (post_logout_redirect_uri), which a fetch would swallow.
function submitLogoutForm(fields: Record<string, string>): void {
  const form = document.createElement("form");

  form.method = "POST";
  form.action = LOGOUT_URL;

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input");

    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}

// Application-controlled logout.
// 0. Checks that this tab actually asked to sign out (LOA-1617). The backend
//    endpoint can no longer be forced cross-site, but this page would do the
//    forcing for an attacker if it signed out on any navigation, so a visit
//    with no recorded intent asks the user first.
// 1. Clears all OAuth tokens and PKCE state manually (NOT via library's logOut()
//    which writes NEW stale state).
// 2. POSTs to the backend logout endpoint, which clears the Django session
//    and redirects onward (post_logout_redirect_uri).
//
// We drive this ourselves instead of using oauthConfig.logoutEndpoint because
// the library stores PKCE state before its logout redirect, causing a "Bad
// authorization state" error when the backend redirects back without a code.
function LogoutPage() {
  const { token } = useContext(AuthContext);
  // "confirm" is for a visit this tab did not start — see consumeLogoutIntent.
  const [status, setStatus] = useState<LogoutStatus>("signing-out");
  const hasIntentRef = useRef<boolean | null>(null);
  const startedRef = useRef(false);

  const signOut = useCallback(
    async function signOut(): Promise<void> {
      // The CSRF token is fetched FIRST: without it the backend refuses the
      // POST, and clearing local state before knowing that would leave the
      // user looking signed out while their Django session is still live.
      const csrfToken = await fetchCsrfToken();

      if (csrfToken === null) {
        // Nothing was torn down, so the retry below is a real retry.
        startedRef.current = false;
        setStatus("failed");

        return;
      }

      // LOA-1613: revoke this session's own access/refresh token before they're
      // wiped below. This is the funnel the 401 handler navigates to directly
      // (skipping useAuth.logout()), so tokens are often still present here —
      // fire-and-forget, since logout must never block/hang on a network call.
      void revokeTokens();
      // Clear ROCP_* tokens and the pending OAuth flow state.
      // CRITICAL: Do NOT call logOut() - it writes NEW stale PKCE state that causes
      // "Bad authorization state" errors in other tabs' fresh logins.
      clearClientAuthState();
      // Allows the next login on this browser to pick a different tenant.
      // LOA-1617: moved here from useAuth.logout with the rest of the
      // destructive cleanup, so nothing is torn down before the POST can be
      // made.
      clearActiveTenantCookie();
      // Clear the rest of sessionStorage (drafts, handoffs) too
      sessionStorage.clear();
      // Persisted ARIES transcripts live in localStorage so they survive a tab
      // close, so nothing above touches them. Purge here rather than only in
      // useAuth.logout: this route is the single funnel for every logout
      // navigation, including the forced one the 401 handler triggers after a
      // server-side revocation. Otherwise the next user to sign in on this
      // origin could resume the previous session's case transcripts.
      purgeAllChatHistory();

      const fields: Record<string, string> = {
        csrfmiddlewaretoken: csrfToken,
        post_logout_redirect_uri: window.location.origin + "/",
      };

      if (token) {
        fields.id_token_hint = token;
      }

      submitLogoutForm(fields);
    },
    [token],
  );

  // One sign-out per mount, whatever starts it: the StrictMode effect replay
  // and an impatient second click on the confirm button must not each submit
  // their own form.
  const start = useCallback(
    function start(): void {
      if (startedRef.current) {
        return;
      }

      startedRef.current = true;
      void signOut();
    },
    [signOut],
  );

  useEffect(() => {
    // The intent is one-shot, and StrictMode runs mount effects twice, so the
    // answer is cached for this mount: re-reading it on the replay would find
    // the marker already spent and prompt the user mid-sign-out. For the same
    // reason it cannot be read during render or from a useState initializer —
    // either can run for a render that never commits.
    hasIntentRef.current ??= consumeLogoutIntent(readLogoutIntentNonce());

    if (!hasIntentRef.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStatus("confirm");

      return;
    }

    start();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleConfirm(): void {
    setStatus("signing-out");
    start();
  }

  if (status === "confirm") {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
          Sign out of LOA on this browser?
        </p>
        <button type="button" onClick={handleConfirm}>
          Sign out
        </button>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
          We could not sign you out. Check your connection and try again.
        </p>
        <button type="button" onClick={handleConfirm}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div style={{ textAlign: "center", padding: "2rem" }}>
      <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>Signing out…</p>
    </div>
  );
}
